import { Card } from "@/components/ui/card";
import { motion } from "@/components/ui/motion-provider";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { Search, BookmarkPlus, History, FileSpreadsheet, Users, UserCircle } from "lucide-react";
import { Typography } from "@/components/ui/typography";

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode;
  image: string;
  width: number;
  height: number;
}

const features: Feature[] = [
  {
    title: "Smart Search",
    description: "Find developers using advanced filters and AI-powered recommendations. Filter by location, tech stack, and more.",
    icon: <Search className="w-6 h-6" />,
    image: "/screenshots/search.png",
    width: 1200,
    height: 675
  },
  {
    title: "List Management",
    description: "Create and organize custom lists of promising developers. Add notes, tags, and track your talent pipeline.",
    icon: <BookmarkPlus className="w-6 h-6" />,
    image: "/screenshots/lists.png",
    width: 1200,
    height: 675
  },
  {
    title: "Saved Searches",
    description: "Save your search criteria and get notified when new developers match your requirements.",
    icon: <History className="w-6 h-6" />,
    image: "/screenshots/results.png",
    width: 1200,
    height: 675
  },
  {
    title: "Data Export",
    description: "Export your search results and lists in CSV format for easy integration with your workflow.",
    icon: <FileSpreadsheet className="w-6 h-6" />,
    image: "/screenshots/export.png",
    width: 1200,
    height: 675
  },
  {
    title: "Organization Insights",
    description: "Explore organizations and their developer networks. Understand team structures and hiring patterns.",
    icon: <Users className="w-6 h-6" />,
    image: "/screenshots/org.png",
    width: 1200,
    height: 675
  },
  {
    title: "Rich Profile Info",
    description: "Get comprehensive developer profiles with skills, experience, contributions, and social presence all in one place.",
    icon: <UserCircle className="w-6 h-6" />,
    image: "/screenshots/profile.png",
    width: 1200,
    height: 675
  }
];

export function FeatureGrid() {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <section className="w-full py-16 sm:py-24">
      <div className="container px-4 md:px-6">
        <div className="flex flex-col items-center gap-4 text-center mb-12 sm:mb-16">
          <motion.h2 
            className={cn(
              "font-bold bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/80",
              isMobile ? "text-3xl" : "text-4xl sm:text-5xl"
            )}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Powerful Features for Developer Search
          </motion.h2>
          <motion.p 
            className="text-muted-foreground max-w-2xl text-base sm:text-lg"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Everything you need to find and organize top developer talent
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="group h-full bg-card hover:bg-card/80 transition-colors p-6 sm:p-8">
                <div className="flex flex-col h-full space-y-6">
                  {/* Icon and Title */}
                  <div className="flex items-center gap-4">
                    <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
                      {feature.icon}
                    </div>
                    <Typography variant="h3" className="font-semibold text-xl sm:text-2xl">
                      {feature.title}
                    </Typography>
                  </div>

                  {/* Description */}
                  <Typography className="text-muted-foreground flex-grow">
                    {feature.description}
                  </Typography>

                  {/* Image */}
                  <div className="relative aspect-[16/9] rounded-xl overflow-hidden bg-muted/50 group-hover:shadow-md transition-shadow">
                    <motion.div
                      className="w-full h-full"
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <img
                        src={feature.image}
                        alt={feature.title}
                        width={feature.width}
                        height={feature.height}
                        className="w-full h-full object-cover object-top"
                        loading={index < 3 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    </motion.div>
                    <div className="absolute inset-0 bg-gradient-to-t from-background/10 to-transparent" />
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
} 